import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Container, Card, Row, Col, Badge, Spinner, Alert } from 'react-bootstrap';
import { useAuth } from '../../contexts/AuthContext';
import { fetchCases } from '../../api/cases';
import { Case } from '../../types';
import { formatDate } from '../../utils/dateFormatters';
import { Briefcase, Calendar, Person, ArrowRight } from 'react-bootstrap-icons';
import 'bootstrap-icons/font/bootstrap-icons.css';

const AttorneyCases: React.FC = () => {
  const auth = useAuth();
  const [cases, setCases] = useState<Case[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadCases = async () => {
      if (!auth.user?.id_token) return;
      
      try {
        setLoading(true);
        setError(null);
        const response = await fetchCases(auth.user.id_token);
        
        // Get user's email from the token
        const userEmail = auth.user.profile?.email?.toLowerCase();
        console.log('Attorney email from token:', userEmail);
        console.log('All cases returned:', response.data.cases || response.data);
        
        // Handle both response formats (cases array or direct array)
        const allCases = response.data.cases || response.data || [];
        
        // Filter to only cases the user is assigned to
        // Check case-insensitively
        const assignedCases = allCases.filter((c: Case) => {
          const assignedUsers = (c.assigned_users || []).map((u: string) => u.toLowerCase());
          const isAssigned = assignedUsers.includes(userEmail || '');
          console.log(`Case ${c.case_id}: assigned_users=${JSON.stringify(c.assigned_users)}, isAssigned=${isAssigned}`);
          return isAssigned;
        });
        
        console.log('Filtered assigned cases:', assignedCases);
        setCases(assignedCases);
      } catch (err: any) {
        console.error('Error loading cases:', err);
        setError('Failed to load cases. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadCases();
  }, [auth.user?.id_token]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'pending': return 'warning';
      case 'closed': return 'secondary';
      default: return 'secondary';
    }
  };

  if (loading) {
    return (
      <Container className="py-5">
        <div className="text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3 text-muted">Loading your cases...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <div className="mb-4">
        <h2 className="mb-1">
          <Briefcase className="me-2" />
          My Cases
        </h2>
        <p className="text-muted">Cases you are assigned to</p>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {cases.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <Card.Body className="text-center py-5">
            <Briefcase size={48} className="text-muted mb-3" />
            <h5>No Cases Assigned</h5>
            <p className="text-muted mb-0">
              You haven't been assigned to any cases yet.
            </p>
            <p className="text-muted">
              Contact your case manager to get access to cases.
            </p>
          </Card.Body>
        </Card>
      ) : (
        <Row>
          {cases.map((caseItem) => (
            <Col md={6} lg={4} key={caseItem.case_id} className="mb-4">
              <Card className="h-100 border-0 shadow-sm hover-shadow" style={{ transition: 'box-shadow 0.2s' }}>
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <Badge bg={getStatusColor(caseItem.status)}>
                      {caseItem.status}
                    </Badge>
                    <small className="text-muted">
                      {caseItem.case_number}
                    </small>
                  </div>
                  
                  <Card.Title className="mb-3">
                    {caseItem.case_title}
                  </Card.Title>
                  
                  <div className="text-muted small mb-3">
                    <div className="mb-1">
                      <Person className="me-1" />
                      {caseItem.client}
                    </div>
                    {caseItem.created_date && (
                      <div className="mb-1">
                        <Calendar className="me-1" />
                        Created {formatDate(caseItem.created_date)}
                      </div>
                    )}
                    {caseItem.created_by && (
                      <div>
                        <Person className="me-1" />
                        by {caseItem.created_by}
                      </div>
                    )}
                  </div>

                  {caseItem.case_type && (
                    <Badge bg="light" text="dark" className="me-1">
                      {caseItem.case_type}
                    </Badge>
                  )}
                </Card.Body>
                <Card.Footer className="bg-white border-top-0">
                  <Link 
                    to={`/attorney/case/${caseItem.case_id}`}
                    className="btn btn-primary w-100 d-flex align-items-center justify-content-center"
                  >
                    Open Case
                    <ArrowRight className="ms-2" />
                  </Link>
                </Card.Footer>
              </Card>
            </Col>
          ))}
        </Row>
      )}
    </Container>
  );
};

export default AttorneyCases;

